/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable max-len */
/* eslint-disable @angular-eslint/no-input-rename */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  WritableSignal,
  signal,
} from '@angular/core';
import { Params } from '@angular/router';
import { Subscription, Observable, Subject, map, takeUntil } from 'rxjs';
import { UserModel } from '@app/shared/models/api/user.model';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';

import {
  logLogin,
  logSignupGoogleRequest,
} from '@app/core/services/google-analytics.lazy';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { GoogleIconComponent } from '../../../shared/components/icons/generated/google/google.component';
import { AppleIconComponent } from '../../../shared/components/icons/generated/apple/apple.component';
// eslint-disable-next-line max-len
import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { NgIf, AsyncPipe } from '@angular/common';

import { FirebaseAuthService } from '@app/akita/firebase/services/firebase-auth.service';
import { EmailPhoneSignInComponent } from '../email-phone-sign-in/email-phone-sign-in.component';
import { PopsyCheckboxComponent } from '../../../components/checkbox/popsy-checkbox.component';
import { NewsletterAPIService } from '@app/akita/api/newsletter/services/newsletter-api.service';
import { AkitaFirebaseQuery } from '@app/akita/firebase/state/firebase.query';

@Component({
  selector: 'app-auth-intro',
  templateUrl: './auth-intro.template.html',
  styleUrls: ['./auth-intro.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    RemoveIconComponent,
    AppleIconComponent,
    GoogleIconComponent,
    AsyncPipe,
    EmailPhoneSignInComponent,
    PopsyCheckboxComponent,
  ],
})
export class AuthIntroComponent implements OnDestroy {
  @Input() public inDialog?: boolean | null = null;
  @Input('next-mode') public nextMode?: string | null = null;
  @Input('success-path') public successPath?: Array<string> | null = null;
  @Input('success-params') public successRedirectQueryParams?: Params | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();

  public shouldSubscribe: WritableSignal<boolean> = signal(true);

  public redirect$: Observable<string>;

  public lang: string;

  public readonly signUpQueryParams$: Observable<Params>;

  public submitted: boolean;
  public failed: boolean;
  public loading: boolean;
  public error?: string | null;

  private readonly langChangesObserver: Subscription | null;
  private userLoginObserver?: Subscription | null;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly firebaseAuthService: FirebaseAuthService,
    private readonly akitaFirebaseQuery: AkitaFirebaseQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaCartService: AkitaCartService,
    private readonly newsletterAPIService: NewsletterAPIService
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();

    this.submitted = false;
    this.loading = false;
    this.failed = false;
    this.redirect$ = this.akitaRouterQuery.selectRedirect();

    this.lang = this.translateService.getActiveLang();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.signUpQueryParams$ = this.akitaRouterQuery.selectQueryParamsToKeep().pipe(
      map((params: Params) => ({
        ...(params || {}),
        ...this.signUpQueryParams,
      }))
    );

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });
  }

  public onHandleRedirect(): void {
    if (
      this.shouldSubscribe() === true &&
      this.akitaFirebaseQuery.user &&
      (this.akitaFirebaseQuery.user.phone || this.akitaFirebaseQuery.user.email)
    ) {
      this.subscribeToNewsletter(
        this.akitaFirebaseQuery.user.email,
        this.akitaFirebaseQuery.user.phone
      );
    }
    this.handleRedirect.emit();
  }

  public onSignWithGoogle(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.changeDetector.markForCheck();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    logSignupGoogleRequest(this.googleAnalyticsService.logEventWrapper);
    // this.userLoginObserver = this.akitaAuthService.signWithGoogleAsync();
    this.userLoginObserver = this.firebaseAuthService
      .signInWithGoogle()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (user: UserModel | null) => {
          setTimeout(() => {
            if (user) {
              logLogin(this.googleAnalyticsService.auth, user, 'google');
              this.submitted = false;
              this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());
              this.onHandleRedirect();
            }
            this.close(false);
          }, 1000);
        },
        error: () => {
          this.setError('UNKNOWN');
          this.failed = true;
          this.submitted = false;
          this.loading = false;
          this.changeDetector.markForCheck();
        },
      });

    return false;
  }

  public onSignWithApple(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.changeDetector.markForCheck();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    this.userLoginObserver = this.firebaseAuthService
      .signInWithApple()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (user: UserModel | null) => {
          setTimeout(() => {
            if (user) {
              logLogin(this.googleAnalyticsService.auth, user, 'apple');
              this.submitted = false;
              this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());
              this.onHandleRedirect();
            }
            this.close(false);
          }, 1000);
        },
        error: () => {
          this.setError('UNKNOWN');
          this.failed = true;
          this.submitted = false;
          this.loading = false;
          this.changeDetector.markForCheck();
        },
      });
    return false;
  }

  private setError(error?: string | null): void {
    this.error = `${error || ''}`;
    this.changeDetector.markForCheck();
  }

  private get signUpQueryParams(): any {
    let params = {};
    const email =
      this.akitaRouterQuery.params.email || this.akitaRouterQuery.queryParams.email || '';
    if (email) {
      params = { ...params, email: email };
    }
    const username =
      this.akitaRouterQuery.params.username ||
      this.akitaRouterQuery.queryParams.username ||
      '';
    if (username) {
      params = { ...params, username: username };
    }
    if (this.akitaRouterQuery.redirect) {
      params = { ...params, redirect: encodeURI(this.akitaRouterQuery.redirect) };
    }
    return params;
  }

  public authUrlFragment(authType: string): Array<string> {
    const fragment = this.akitaRouterQuery.userUrlFragment;
    fragment.push(authType || 'sign-up');
    return fragment;
  }

  public onEmailPhoneSubmitting($event: boolean): void {
    this.submitted = $event;
    this.loading = $event;
    this.failed = false;
    this.changeDetector.markForCheck();
  }

  public changeDialogMode(mode?: string | null): boolean {
    const username: string | null =
      this.akitaRouterQuery.params.username ||
      this.akitaRouterQuery.queryParams.username ||
      null;
    const email: string | null =
      this.akitaRouterQuery.params.email ||
      this.akitaRouterQuery.queryParams.email ||
      null;

    this.dialogModeChange.emit({
      mode: mode || 'auth-intro',
      email: email,
      username: username,
      previousMode: 'auth-intro',
    });

    return false;
  }

  public subscribeToNewsletter(email: string | null, phone: string | null): boolean {
    const country = this.akitaRouterQuery.country;

    this.subscriptions.add(
      this.newsletterAPIService
        .signUpToNewsletter(country, email, phone)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {},
          error: () => {},
        })
    );

    return false;
  }

  public close(dismiss?: boolean): boolean {
    this.closeDialog.emit(dismiss);
    return false;
  }

  public stopPropagation(): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }
  }
}
